import { createFormErrorMessage } from '@/components/FormErrorMessage';
import settings, { EMAIL_FALLBACK_ERROR } from '@/constants/constants';
import { medicalConditions } from '@/data/PreConsultation/medication-data';
import { getOptionalEmailCollectionStep, getPreConsultationSteps } from '@/data/PreConsultation/pre-consultation-steps';
import { useDatadogForFormsUserMetadata } from '@/hooks/useDatadogForFormsUserMetadata';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import { Box, Typography } from '@montugroup/design-system';
import type { Step } from '@montugroup/form-builder';
import { FormBuilder } from '@montugroup/form-builder';
import type { SubmitPreConsultationFormSchema } from '@montugroup/pms-api-contracts';
import { Logger } from '@montugroup/utilities';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import type { z } from 'zod';
import { WithPageWrapper } from '../components/Branding';

const logger = new Logger('PreConsultationForm');

export type PreConsultationFormProps = {
  pageWrapper?: boolean;
  userData?: {
    email?: string;
  };
  showLogo?: boolean;
};

type PreConsultationRequestBody = { data: z.input<typeof SubmitPreConsultationFormSchema> };

type StepIndicatorParams = {
  currentStep: number;
  totalSteps: number;
  stepLabel?: string;
};

type Treatment = {
  name: string;
  frequency?: string;
  dosage?: string;
  duration?: string;
};

type DurationItem = {
  name: string;
  duration?: string;
};

const StepIndicator = ({ currentStep, totalSteps }: StepIndicatorParams) => {
  return <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{`Question ${currentStep} of ${totalSteps}`}</Typography>;
};

// The emergency contact step, which the API rejects a submission over when the
// number matches the patient's own.
const PreConsultErrorMessage = createFormErrorMessage({
  "the emergency contact number you've entered cannot be used. please provide a different one.": 4,
});

export const PreConsultationForm = ({ pageWrapper = true, userData, showLogo = true }: PreConsultationFormProps) => {
  const POST_URL = `${settings.xApiUrl}/patient-portal/v1/pre-consultation-form`;
  const FORM_ID = 'pre-consultation-variant-new';

  const location = useLocation();
  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();

  // Use email from userData (from PCP) if available, otherwise from query param (from Pre-screening)
  const emailFromParam = new URLSearchParams(location.search).get('email');
  const rawEmail = userData?.email ?? emailFromParam;
  const shaUserEmail = rawEmail ? formatStringToSha256Hash(rawEmail) : null;

  useDatadogForFormsUserMetadata({
    hashedEmail: shaUserEmail ?? undefined,
  });
  const needsEmailStep = !rawEmail || rawEmail === EMAIL_FALLBACK_ERROR;
  const preConsultationSteps = getPreConsultationSteps(showLogo);
  const optionalEmailCollectionStep = getOptionalEmailCollectionStep(showLogo);
  const formSteps = needsEmailStep
    ? [preConsultationSteps[0], optionalEmailCollectionStep, ...preConsultationSteps.slice(1)]
    : preConsultationSteps;

  useEffect(() => {
    if (rawEmail === EMAIL_FALLBACK_ERROR) {
      logger.warn('Pre-consult form loaded with a fallback email');
    }
  }, [rawEmail]);

  const formatPreConsultDataToSha256HashedValues = (formValues: FieldValues) => {
    const formValuesData = formValues.values.data;

    const userEmail = formValuesData.email;
    const userPostcode = formValuesData.address.postcode;
    const userCity = formValuesData.address.suburb;
    const userState = formValuesData.address.state;

    return {
      ...(userEmail ? { sha_email: formatStringToSha256Hash(userEmail) } : {}),
      ...(userPostcode ? { sha_postcode: formatStringToSha256Hash(userPostcode) } : {}),
      ...(userCity ? { sha_city: formatStringToSha256Hash(userCity) } : {}),
      ...(userState
        ? {
            sha_gads_region: formatStringToSha256Hash(userState),
            sha_fb_region: formatStringToSha256Hash(userState),
          }
        : {}),
    };
  };

  const logEvents = (event: string, formValues: FieldValues) => {
    const stepId = formValues.current;
    const isPreconsultComplete = event === 'submission_success';

    // Handle "preconsult complete" event
    if (isPreconsultComplete) {
      const payload = {
        clickId: 'treatments-tried',
        event: 'preconsult_complete',
        step_name: "We've received your Pre-Consult",
        step_number: '11',
        // Includes 'enchanced conversion' SHA values (sha_fb_region, sha_gads_region etc)
        ...formatPreConsultDataToSha256HashedValues(formValues),
      };

      sendGoogleAnalyticsEvent(event, payload);
    }

    // An ID of 0 is used to not interfere with historical data
    // so we send it manually to still track the conditional step
    if (needsEmailStep && stepId === 0) {
      const { trackingData } = optionalEmailCollectionStep;
      const emailInput = formValues.values?.email;

      sendGoogleAnalyticsEvent(event, {
        clickId: trackingData?.clickId ?? null,
        event: trackingData?.event ?? null,
        step_name: trackingData?.stepName ?? null,
        sha_email: emailInput ? formatStringToSha256Hash(emailInput) : null,
        step_number: '0',
      });
      return;
    }

    // Handle normal step event (called after the completion of the step)
    const prevTrackingData = preConsultationSteps[stepId - 1]?.trackingData;

    if (!prevTrackingData) {
      return;
    }

    const prevPayload = {
      clickId: prevTrackingData.clickId ?? null,
      event: prevTrackingData.event ?? null,
      step_name: prevTrackingData.stepName ?? null,
      sha_email: shaUserEmail,
      step_number: String(stepId),
    };

    sendGoogleAnalyticsEvent(event, prevPayload);
  };

  const handleTransform = (formValues: FieldValues): PreConsultationRequestBody => {
    const { has_medicare, ...values } = formValues;

    const formatMedicalNotes = (treatments: Treatment[], valueKeys: (keyof Treatment)[]) =>
      treatments && treatments.length
        ? treatments.map((treatment) => `- ${valueKeys.map((key) => treatment[key]).join(', ')}`).join('\n')
        : '';

    const emergencyContact = {
      fullName: `${values.emergencyContact.firstName} ${values.emergencyContact.lastName}`.trim(),
      relationship: values.emergencyContact.relationship,
      phoneNo: values.emergencyContact.phoneNo,
    };

    /*
      Below, we take the duration from the 'Primary condition' step in the form,
      and use it on the healthConcerns values, so that it can be formatted
      into the 'listOfConditions' notes. This is because clinic only cares
      about the duration of the primary health condition, not on the secondary conditions.
    */
    const medicalCondition = medicalConditions.find((condition) => condition.label === values.primaryConcern.name);
    // concerns can be { query: '' } from the multi select if it'sleft empty..
    const healthConcerns = Array.isArray(values.healthConcerns?.concerns) ? values.healthConcerns.concerns : [];
    // Add duration to the primary condition
    const withDuration: DurationItem[] = healthConcerns.map((item: DurationItem) =>
      item.name === medicalCondition?.label ? { ...item, duration: values.primaryConcern.duration } : item,
    );
    // Move the primary condition to the front of the array
    const healthConcernsWithDuration = [
      ...withDuration.filter((item) => item.name === medicalCondition?.label),
      ...withDuration.filter((item) => item.name !== medicalCondition?.label),
    ];
    // Format to medical notes
    const listOfConditions = formatMedicalNotes(healthConcernsWithDuration, ['name', 'duration']);

    const previousTreatments = formatMedicalNotes(values.previousMedications?.medication, [
      'name',
      'dosage',
      'frequency',
    ]);
    const currentTreatments = formatMedicalNotes(values.currentMedications?.medication, [
      'name',
      'dosage',
      'frequency',
    ]);
    const primaryCondition = String(medicalCondition?.id ?? '');

    const medicareInfo =
      has_medicare === 'true'
        ? {
            number: values.medicare.number,
            referenceNo: values.medicare.referenceNo,
            fullName: values.medicare.fullName,
            expiryDate: DateTime.fromISO(values.medicare.expiryDate).toISODate()!,
          }
        : undefined;

    const transformed: PreConsultationRequestBody = {
      data: {
        birthday: DateTime.fromISO(values.birthday).toISODate()!,
        address: values.address,
        gender: values.gender,
        emergencyContact,
        currentTreatments,
        previousTreatments,
        primaryCondition,
        email: needsEmailStep ? values.email : rawEmail,
        medicare: medicareInfo,
        // Below is a workaround so we can pass a single string of formatted conditions (but keep format for endpoint)
        listOfConditions: [listOfConditions],
      },
    };

    return transformed;
  };

  return WithPageWrapper(
    <Box
      sx={
        pageWrapper
          ? {
              paddingTop: {
                md: '1.5rem',
                xs: '1rem',
              },
              paddingBottom: '3rem',
            }
          : {}
      }
    >
      <FormBuilder
        formId={FORM_ID}
        postUrl={POST_URL}
        steps={formSteps as Step[]}
        logEvents={logEvents}
        transform={handleTransform}
        errorSlot={PreConsultErrorMessage}
        stepButtonsConfig={{
          fullWidthStepButtons: true,
        }}
        showStepIndicator={({ currentStep, totalSteps }: StepIndicatorParams) => (
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
        )}
      />
    </Box>,
    pageWrapper,
  );
};
